import {
  aggregate,
  formatCents,
  METRIC_IDS,
  METRICS,
  type Cents,
  type Metric,
  type ReportDefinition,
} from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { useDebounced } from '../people/usePeople.ts'
import type { Permission } from '@church/permissions'
import { useAccess } from '../access/useAccess.ts'
import { platformKeys } from './keys.ts'

/** The home dashboard and the report builder. Both read the read model only. */

export function useDashboard(campusId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: platformKeys.dashboard(campusId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.insights.dashboard.get({
        query: campusId ? { campusId } : {},
      })
      if (error) throw error
      return data
    },
    staleTime: 60_000,
  })
}

/** "$12,480", "1,204" — a metric's value as the dashboard shows it. */
export function formatMetric(metric: string, value: number): string {
  const def = METRICS[metric as Metric]
  if (def?.unit === 'cents')
    return formatCents((Math.round(value / 100) * 100) as Cents).replace(/\.00$/, '')
  return value.toLocaleString()
}

/** Metrics this viewer may put in a report. */
export function useAllowedMetrics(): Metric[] {
  const access = useAccess()
  // ForChurch's metrics through the ladders (Viewer of the module); giving's through its role.
  return METRIC_IDS.filter((m) => access.allows(METRICS[m].permission as Permission))
}

export function useRefreshInsights() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.insights.refresh.post()
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: platformKeys.all() }),
  })
}

const DEFAULT: ReportDefinition = {
  metrics: ['service_attendance', 'new_people'],
  interval: 'week',
  range: { kind: 'last', count: 12, unit: 'weeks' },
  campusId: null,
}

/** base64url JSON — how a definition rides in a download link. */
export const encodeDefinition = (definition: ReportDefinition) =>
  btoa(String.fromCharCode(...new TextEncoder().encode(JSON.stringify(definition))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')

/**
 * The report builder: a definition being edited, its result (debounced, from
 * the server), and save/schedule. `preview` re-aggregates the server's rows
 * client-side with the same `aggregate` the server uses.
 */
export function useReportBuilder(initial?: ReportDefinition) {
  const api = useApi()
  const queryClient = useQueryClient()
  const allowed = useAllowedMetrics()
  const [definition, setDefinition] = useState<ReportDefinition>(
    initial ?? { ...DEFAULT, metrics: DEFAULT.metrics.filter((m) => allowed.includes(m)) },
  )
  const debounced = useDebounced(definition, 300)
  const result = useQuery({
    queryKey: platformKeys.report(debounced),
    enabled: debounced.metrics.length > 0,
    queryFn: async () => {
      const { data, error } = await api.api.v1.reports.run.post({ definition: debounced })
      if (error) throw error
      return data
    },
    placeholderData: (previous) => previous,
  })
  const saved = useQuery({
    queryKey: platformKeys.reports(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.reports.get()
      if (error) throw error
      return data
    },
  })
  const save = useMutation({
    mutationFn: async (input: {
      id?: string
      name: string
      schedule: 'weekly' | 'monthly' | null
      format: 'csv' | 'xlsx' | 'pdf'
      recipients: string[]
    }) => {
      const { data, error } = await api.api.v1.reports.post({ ...input, definition })
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(platformKeys.reports(), data),
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.reports({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => queryClient.setQueryData(platformKeys.reports(), data),
  })

  const toggleMetric = (metric: Metric) =>
    setDefinition((d) => ({
      ...d,
      metrics: d.metrics.includes(metric)
        ? d.metrics.filter((m) => m !== metric)
        : [...d.metrics, metric],
    }))

  return {
    definition,
    setDefinition,
    toggleMetric,
    setInterval: (interval: ReportDefinition['interval']) =>
      setDefinition((d) => ({ ...d, interval })),
    setRange: (range: ReportDefinition['range']) => setDefinition((d) => ({ ...d, range })),
    setCampus: (campusId: string | null) => setDefinition((d) => ({ ...d, campusId })),
    allowed,
    result: result.data ?? null,
    isRunning: result.isFetching,
    error: result.error ?? save.error ?? remove.error,
    saved: saved.data ?? [],
    save,
    remove,
    /** A link that downloads the current report. */
    downloadPath: (format: 'csv' | 'xlsx' | 'pdf', name = 'Report') =>
      `/api/v1/reports/export?format=${format}&name=${encodeURIComponent(name)}&d=${encodeDefinition(definition)}`,
    /** The same table the server computed, for charts: the client never invents numbers. */
    emptyTable: () => aggregate(definition, [], new Date().toISOString().slice(0, 10)),
  }
}
